import React, { useState } from "react";
import { Select } from "antd";
import Alert from "../components/Alert";
import StepFooter from "./StepFooter";
import { useIsMobile } from "Hooks";
import { Icon } from "Components";
import OrganizationRecipients from "./Step3SelectionMethods/OrganizationRecipients";
import RoleRecipients from "./Step3SelectionMethods/RoleRecipients";
import PropertyRecipients from "./Step3SelectionMethods/PropertyRecipients";

interface StepProps {
  onBack: () => void;
  onNext: () => void;
}

type SelectionMethod =
  | "organization"
  | "property"
  | "role"
  | "group"
  | "individual"
  | "";

interface SelectorOption {
  key: SelectionMethod;
  icon: React.ReactNode;
  label: string;
  description: string;
}

const selectionOptions: SelectorOption[] = [
  {
    key: "organization",
    icon: <Icon type="recipientBuilding" />,
    label: "By Organization",
    description: "Send to all users in your organization",
  },
  {
    key: "property",
    icon: <Icon type="recipientProperty" />,
    label: "By Property",
    description: "Select entire properties",
  },
  {
    key: "role",
    icon: <Icon type="recipientRole" />,
    label: "By Role",
    description: "Send to all users assigned to a specific role",
  },
  {
    key: "group",
    icon: <Icon type="recipientGroup" />,
    label: "By Group",
    description: "Select groups or departments",
  },
  {
    key: "individual",
    icon: <Icon type="recipientIndividual" />,
    label: "By Individual",
    description: "Select specific individuals by name or email",
  },
];

const roleOptions = [
  {
    key: "staff",
    label: "Staff",
    count: 40,
    description: "All users other than Managers and Leadership team",
  },
  {
    key: "manager",
    label: "Manager",
    count: 5,
    description: "Department and property managers",
  },
  {
    key: "leadership",
    label: "Leadership and IT",
    count: 2,
    description: "Those overseeing system and strategy",
  },
];

const propertyOptions = [
  { value: "All", label: "All properties (1,570)", count: 1570 },
  { value: "premier-inn", label: "Premier Inn (520)", count: 520 },
  { value: "plaza-hotel", label: "Plaza Hotel (480)", count: 480 },
  { value: "royal-sunset", label: "Royal Sunset Hotel (80)", count: 80 },
  { value: "central-business", label: "Central Business Hotel (80)", count: 80 },
  { value: "grand-palace", label: "Grand Palace Resort (410)", count: 410 }
];

const propertyRoles: Record<string, { key: string; count: number }[]> = {
  all: [
    { key: "staff", count: 1210 },
    { key: "manager", count: 280 },
    { key: "leadership", count: 80 },
  ],
  "premier-inn": [
    { key: "staff", count: 430 },
    { key: "manager", count: 70 },
    { key: "leadership", count: 20 },
  ],
  "plaza-hotel": [
    { key: "staff", count: 390 },
    { key: "manager", count: 70 },
    { key: "leadership", count: 20 },
  ],
  "royal-sunset": [
    { key: "manager", count: 80 },
  ],
  "central-business": [
    { key: "manager", count: 60 },
    { key: "leadership", count: 20 },
  ],
  "grand-palace": [
    { key: "staff", count: 390 },
    { key: "leadership", count: 20 },
  ],
};

const Step3Recipients: React.FC<StepProps> = ({ onBack, onNext }) => {
  const [selectedMethod, setSelectedMethod] = useState<SelectionMethod>("");
  const [organizationSelected, setOrganizationSelected] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<string | undefined>(undefined);
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [propertySearchQuery, setPropertySearchQuery] = useState("");

  const isMobile = useIsMobile();

  const handleMethodChange = (method: SelectionMethod) => {
    setSelectedMethod(method);
    setOrganizationSelected(false);
    setSelectedRoles([]);
    setSelectedProperty(undefined);
    setRoleSearchQuery("");
    setSelectedProperties([]);
    setPropertySearchQuery("");
  };

  return (
    <>
      <div className="step-card phishing-step3">
        <div className="card-header">
          <div className="card-title">Full Campaign Recipients</div>
          <div className="card-subtitle">
            Select recipients to receive the phishing campaign once testing is complete
          </div>
        </div>

        <div className="phishing-step3__selection">
          <div className="phishing-step3__label">
            Selection method <span className="required">*</span>
          </div>
          {isMobile ? (
            <div className="phishing-step3__dropdown--mobile">  
              <Select
                style={{ width: "100%" }}
                value={selectedMethod || undefined}
                dropdownClassName="campaign-setup-filter-option-select"
                onChange={(value) => handleMethodChange(value)}
                options={selectionOptions.map((opt) => ({
                  value: opt.key,
                  label: opt.label,
                }))}
                placeholder="Select a method"
                size="large"
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </div>
          ) : (
            <div className="phishing-step3__grid">
              {selectionOptions.map((opt) => {
                const active = selectedMethod === opt.key;
                return (
                  <div
                    key={opt.key}                    
                    onClick={() => handleMethodChange(opt.key)}
                    className={`phishing-step3__selector ${active ? "phishing-step3__selector--active" : ""
                      }`}
                  >
                    <div className="phishing-step3__selector-header">
                      <div className="phishing-step3__icon">{opt.icon}</div>
                      <span className="phishing-step3__selector-title">
                        {opt.label}
                      </span>
                    </div>
                    <span className="phishing-step3__selector-desc">
                      {opt.description}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selectedMethod === "organization" && (
          <OrganizationRecipients
            organizationSelected={organizationSelected}
            setOrganizationSelected={setOrganizationSelected}
          />
        )}

        {selectedMethod === "role" && (
          <RoleRecipients
            propertyOptions={propertyOptions}
            roleOptions={roleOptions}
            propertyRoles={propertyRoles}
            selectedRoles={selectedRoles}
            setSelectedRoles={setSelectedRoles}
            selectedProperty={selectedProperty}
            setSelectedProperty={setSelectedProperty}
            roleSearchQuery={roleSearchQuery}
            setRoleSearchQuery={setRoleSearchQuery}
          />
        )}

        {selectedMethod === "property" && (
          <PropertyRecipients
            propertyOptions={propertyOptions}
            selectedProperties={selectedProperties}
            setSelectedProperties={setSelectedProperties}
            propertySearchQuery={propertySearchQuery}
            setPropertySearchQuery={setPropertySearchQuery}
          />
        )}

        <div className="alert-section">
          <Alert title="Information" subtitle="No emails will be sent to these recipients until the test is completed and the full campaign is approved" />
        </div>
      </div>
      <StepFooter
        onBack={onBack}
        onNext={onNext}
        nextBtnDisabled={
          !!selectedMethod ?
          selectedMethod === "organization"
            ? !organizationSelected
            : selectedMethod === "role"
              ? selectedRoles.length === 0
              : selectedMethod === "property"
                ? selectedProperties.length === 0
                : false : true
        }
      />
    </>
  );
};

export default Step3Recipients;